// Event index: Kalshi has no text search, so the open-event list is held and matched locally in the page.

import { getMeta, setMeta } from './db';
import { logInfo, logWarn } from './log';
import type { KalshiClient } from '../kalshi/client';

export interface EventRow {
  ticker: string;
  title: string;
  subTitle: string;
  category: string;
  /** Series this event belongs to; the key into `series`. */
  series: string;
}

/** Only the two fields worth searching, for series that actually have events. */
export interface SeriesRow {
  title: string;
  tags: string[];
}

interface CachedIndex {
  builtAt: number;
  rows: EventRow[];
  series: Record<string, SeriesRow>;
}

const META_KEY = 'events_index_v2';
/** Events open and close all day; an hour-old index is fine for finding one. */
const TTL_MS = 60 * 60 * 1000;
const PAGE = 200;
// Pages are not sorted by anything useful; a lower cap can silently truncate whole series out of the index.
const MAX_PAGES = 80;

let rows: EventRow[] = [];
let series: Record<string, SeriesRow> = {};
let builtAt = 0;
let building: Promise<void> | null = null;

export function eventRows(): readonly EventRow[] {
  return rows;
}

/** The series an event belongs to — its title is what users actually search. */
export function seriesOf(e: EventRow): SeriesRow | undefined {
  return series[e.series];
}

export function indexAge(): number | null {
  return builtAt ? Date.now() - builtAt : null;
}

export function isBuilding(): boolean {
  return building !== null;
}

/** Load whatever was cached last time, local only (no network); never rejects — a failed load just means no cache. */
export async function warmEventIndex(): Promise<void> {
  try {
    const cached = await getMeta<CachedIndex | null>(META_KEY, null);
    if (cached?.rows?.length) {
      rows = cached.rows;
      series = cached.series ?? {};
      builtAt = cached.builtAt;
      const mins = Math.round((Date.now() - builtAt) / 60000);
      logInfo('events', `index loaded from cache — ${rows.length} events, ${mins}m old`);
    }
  } catch (e) {
    logWarn('events', `cache read failed: ${(e as Error).message}`);
  }
}

/** Rebuild if the index is older than the TTL; caller must run this on a timer, not just once at boot. */
export function refreshEventIndex(client: KalshiClient): void {
  if (building) return;
  if (Date.now() - builtAt <= TTL_MS) return;
  void rebuildEventIndex(client);
}

/** Page the whole open-event list. Concurrent calls share one pass. */
export function rebuildEventIndex(client: KalshiClient): Promise<void> {
  if (building) return building;
  building = (async () => {
    const t0 = Date.now();
    logInfo('events', 'building search index…');
    const out: EventRow[] = [];
    let cursor = '';
    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const data = await client.getEvents({
          limit: PAGE,
          status: 'open',
          ...(cursor ? { cursor } : {}),
        });
        for (const e of data.events ?? []) {
          out.push({
            ticker: String(e.event_ticker ?? ''),
            title: String(e.title ?? ''),
            subTitle: String(e.sub_title ?? ''),
            category: String(e.category ?? ''),
            series: String(e.series_ticker ?? ''),
          });
        }
        cursor = String(data.cursor ?? '');
        if (!cursor || !(data.events ?? []).length) break;
        if (page === MAX_PAGES - 1) {
          logWarn('events', `stopped at the ${MAX_PAGES}-page cap with more events left — `
            + 'search will be missing the tail');
        }
      }
      if (out.length) {
        const map = await fetchSeries(client, new Set(out.map((e) => e.series)));
        rows = out;
        series = map;
        builtAt = Date.now();
        await setMeta(META_KEY, { builtAt, rows, series } satisfies CachedIndex);
        logInfo('events', `index ready — ${rows.length} events, `
          + `${Object.keys(series).length} series in ${((Date.now() - t0) / 1000).toFixed(1)}s`);
      } else {
        logWarn('events', 'index build returned nothing; keeping previous index');
      }
    } catch (e) {
      // Ticker search still works without this, so a failure is not fatal.
      logWarn('events', `index build failed: ${(e as Error).message}`);
    } finally {
      building = null;
    }
  })();
  return building;
}

/** Series titles and tags, kept only for series with an open event; this is what makes category words match. */
async function fetchSeries(client: KalshiClient, used: Set<string>): Promise<Record<string, SeriesRow>> {
  try {
    const all = await client.getSeries();
    const map: Record<string, SeriesRow> = {};
    for (const s of all) {
      const key = String(s.ticker ?? '');
      if (!used.has(key)) continue; // ~12.5k series exist; only the live ones matter
      map[key] = { title: String(s.title ?? ''), tags: (s.tags ?? []).map(String) };
    }
    return map;
  } catch (e) {
    logWarn('events', `series fetch failed, search will miss category words: ${(e as Error).message}`);
    return series;
  }
}

/** True when the query appears as a whole word, not just as a substring inside a longer word. */
const edge = (c: string | undefined) => c === undefined || !/[a-z0-9]/.test(c);

function wordHit(hay: string, needle: string): boolean {
  for (let i = hay.indexOf(needle); i !== -1; i = hay.indexOf(needle, i + 1)) {
    if (edge(hay[i - 1]) && edge(hay[i + needle.length])) return true;
  }
  return false;
}

/** Substring match over title, sub-title, ticker, series title and tags, ranked event text above series above ticker. */
export function searchEvents(q: string, limit = 50): { hits: EventRow[]; total: number } {
  limit = Math.max(1, limit);
  const needle = q.trim().toLowerCase();
  if (needle.length < 2 || !rows.length) return { hits: [], total: 0 };

  // Short queries (<=4 chars) require a word boundary, tag or ticker hit; longer ones allow bare substrings.
  const shortQ = needle.length <= 4;

  const scored: { row: EventRow; score: number }[] = [];
  for (const row of rows) {
    const title = row.title.toLowerCase();
    const sub = row.subTitle.toLowerCase();
    const s = series[row.series];
    const sTitle = (s?.title ?? '').toLowerCase();
    const tags = s?.tags.map((t) => t.toLowerCase()) ?? [];

    let score = 0;
    if (title.startsWith(needle) && edge(title[needle.length])) score = 100;
    else if (wordHit(title, needle)) score = 85;
    else if (tags.some((t) => t === needle)) score = 70;
    else if (!shortQ && title.includes(needle)) score = 65;
    else if (wordHit(sub, needle)) score = 60;
    // Series match ranks below the event's own text but above ticker/category.
    else if (wordHit(sTitle, needle)) score = 52;
    else if (!shortQ && sub.includes(needle)) score = 46;
    else if (tags.some((t) => wordHit(t, needle))) score = 44;
    else if (!shortQ && sTitle.includes(needle)) score = 34;
    else if (row.ticker.toLowerCase().includes(needle)) score = 30;
    else if (row.category.toLowerCase().includes(needle)) score = 20;
    if (score) scored.push({ row, score });
  }

  scored.sort((a, b) => b.score - a.score || a.row.title.localeCompare(b.row.title));
  // `total` lets the UI report a cap instead of a silent truncation.
  return { hits: scored.slice(0, limit).map((x) => x.row), total: scored.length };
}
